import { Ban, Check, Info, Settings2, X } from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS = {
  works: { label: "Works", className: "bg-primary text-primary-foreground" },
  setup: { label: "Works after setup", className: "bg-accent text-accent-foreground" },
};

export const StatusPill = ({ status, testId }) => (
  <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold", STATUS[status].className)} data-testid={testId}>
    <Check className="h-3.5 w-3.5" />{STATUS[status].label}
  </span>
);

export const SupportedCard = ({ item, testId }) => (
  <div className="rounded-2xl border bg-card p-5 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-primary/50" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display text-lg font-bold leading-snug">{item.name}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{item.detail}</p>
      </div>
      <StatusPill status={item.status} testId={`${testId}-status`} />
    </div>
    <ul className="mt-4 space-y-1.5 text-sm">
      {item.notes.map((n) => (
        <li key={n} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />{n}</li>
      ))}
    </ul>
  </div>
);

export const Tip = ({ children, testId }) => (
  <p className="flex gap-2.5 rounded-2xl bg-secondary px-4 py-3 text-sm text-secondary-foreground" data-testid={testId}>
    <Info className="mt-0.5 h-4 w-4 shrink-0" />{children}
  </p>
);

export const NotSupported = ({ items, testId }) => (
  <div className="rounded-2xl border border-dashed p-5" data-testid={testId}>
    <p className="flex items-center gap-2 text-sm font-bold"><Ban className="h-4 w-4 text-destructive" />Not supported</p>
    <ul className="mt-3 divide-y">
      {items.map((i, n) => (
        <li key={i.name} className="grid gap-1 py-3 last:pb-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] sm:gap-6" data-testid={`${testId}-${n}`}>
          <span className="text-sm font-semibold">{i.name}</span>
          <span className="text-sm text-muted-foreground">{i.why}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CAP = {
  yes: { icon: Check, text: "Yes", className: "text-primary" },
  no: { icon: X, text: "No", className: "text-muted-foreground" },
  setting: { icon: Settings2, text: "After a setting", className: "text-accent dark:text-primary" },
};

const Capability = ({ label, value, testId }) => {
  const c = CAP[value];
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl bg-muted/50 px-3 py-2 sm:block sm:bg-transparent sm:p-0" data-testid={testId} data-value={value}>
      <span className="text-xs text-muted-foreground sm:hidden">{label}</span>
      <span className={cn("flex items-center gap-1.5 text-sm font-semibold", c.className)}><c.icon className="h-4 w-4" />{c.text}</span>
    </div>
  );
};

export const BrowserTable = ({ rows }) => (
  <div className="overflow-hidden rounded-2xl border bg-card" data-testid="browser-table">
    <div className="hidden grid-cols-[1.6fr_repeat(3,1fr)] gap-4 border-b bg-muted/40 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
      <span>Browser</span><span>Set up &amp; save</span><span>Connect &amp; drive</span><span>Install firmware</span>
    </div>
    {rows.map((b) => (
      <div key={b.id} className="grid gap-3 border-b px-5 py-4 last:border-b-0 sm:grid-cols-[1.6fr_repeat(3,1fr)] sm:items-center sm:gap-4" data-testid={`browser-row-${b.id}`}>
        <div>
          <p className="font-semibold">{b.name}</p>
          <p className="text-sm text-muted-foreground">{b.platforms}</p>
          {b.note && <p className="mt-1 break-words text-xs text-muted-foreground">{b.note}</p>}
        </div>
        <Capability label="Set up & save" value={b.code} testId={`browser-row-${b.id}-code`} />
        <Capability label="Connect & drive" value={b.connect} testId={`browser-row-${b.id}-connect`} />
        <Capability label="Install firmware" value={b.firmware} testId={`browser-row-${b.id}-firmware`} />
      </div>
    ))}
  </div>
);
